import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useParams, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope } from '@fortawesome/pro-solid-svg-icons/faEnvelope';

import {
  namespaces,
  useSharedTranslation,
} from 'us-react-components/dist/hooks/useSharedTranslation';
import { MessageThreadCard } from 'us-react-components/dist/MessageThreadCard/MessageThreadCard';
import {
  UserNameFilter,
  UserNameFilterOption,
} from 'us-react-components/dist/UserNameFilter/UserNameFilter';
import { Pagination } from 'us-react-components/dist/Pagination/Pagination';
import { MessageThread, MessageThreadService } from 'us-web-services';

import { useDependencies } from '../../dependencies';

const MAX_PREVIEW = 100;

const truncate = (s?: string, n: number = MAX_PREVIEW): string => {
  if (!s) return '';
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
};

export const Messages = observer((): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const { id: pathId } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { stores } = useDependencies();
  const user = stores.user.user;

  const [threads, setThreads] = useState<{
    data: MessageThread[];
    meta?: { size?: number; pageCount?: number };
  }>({ data: [] });
  const [isLoading, setIsLoading] = useState(true);

  const page = Number(searchParams.get('page') || 1) || 1;
  const parentIdParam = searchParams.get('parentId');


  useEffect(() => {
    document.title = 'UrbanSitter - Messages';
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    const sitterId = Number(pathId) || user.id;
    if (sitterId !== user.id) {
      window.location.href = `/app/sitter/${user.id}/messages`;
      return;
    }
    const params: Record<string, unknown> = {
      page,
      sitterId,
      userId: user.id,
      sort: 'modified',
      'modified.dir': 'desc',
    };
    if (parentIdParam) params.parentId = Number(parentIdParam);

    setIsLoading(true);
    const load = async (): Promise<void> => {
      try {
        const resp = await MessageThreadService.getByFilter({ params });
        setThreads({
          data: resp.data?.data ?? [],
          meta: resp.data?.meta,
        });
      } catch (err) {
        console.warn('[Messages] fetch threads failed', err);
      } finally {
        setIsLoading(false);
      }
    };
    void load();
  }, [user?.id, pathId, page, parentIdParam]);

  const setPage = (next: number): void => {
    setSearchParams((sp) => {
      const n = new URLSearchParams(sp);
      n.set('page', String(next));
      return n;
    }, { replace: true });
  };

  const onFilterUserChange = (u: UserNameFilterOption | null): void => {
    setSearchParams(
      (sp) => {
        const n = new URLSearchParams(sp);
        if (u?.id) n.set('parentId', String(u.id));
        else n.delete('parentId');
        n.set('page', '1');
        return n;
      },
      { replace: true },
    );
  };

  const totalPages = threads.meta?.pageCount ?? 1;
  const hasAny = threads.data.length > 0;
  const hasFilter = !!parentIdParam;
  const showFilter = hasAny || hasFilter;

  return (
    <div className='container py-8'>
      <h1 className='text-2xl font-semibold mb-3'>{t('pages.messages.heading')}</h1>

      {showFilter && (
        <div className='grid grid-cols-1 sm:grid-cols-12 gap-4 mb-4'>
          <div className='sm:col-span-6 md:col-span-4'>
            <UserNameFilter
              kind='parent'
              viewerId={user?.id}
              label={t('common.familyLabel')}
              onSelect={onFilterUserChange}
            />
          </div>
        </div>
      )}

      {isLoading && (
        <h2 className='text-base'>
          <i className='fa-solid fa-spinner fa-spin mr-2' />
          {t('pages.messages.loading')}
        </h2>
      )}

      {!isLoading && !hasAny && (
        <div className='text-center max-w-lg mx-auto py-8'>
          <FontAwesomeIcon icon={faEnvelope} className='text-5xl text-neutral-400 mb-4' aria-hidden='true' />
          <h4 className='font-semibold mb-2'>{t('pages.messages.emptyHeading')}</h4>
          <div>
            {t('pages.messages.emptyPrefix')}{' '}
            <a id='go-to-job-board' href='/app/job-board' className='underline'>
              {t('pages.messages.emptyJobBoardLink')}
            </a>
            ,{' '}
            <a id='add-availability' href='/app/sitter-calendar' className='underline'>
              {t('pages.messages.emptyCalendarLink')}
            </a>{' '}
            {t('pages.messages.emptyCalendarSuffix')}{' '}
            {t('pages.messages.and')}{' '}
            <a id='sitter-profile' href={`/app/sitter/${user?.id}`} className='underline'>
              {t('pages.messages.emptyProfileLink')}
            </a>
            .
          </div>
        </div>
      )}

      {!isLoading && hasAny && (
        <div className='max-w-4xl'>
          <ul className='space-y-3 list-none p-0'>
            {threads.data.map((thread) => {
              const sitter = thread.sitter;
              const parent = thread.parent;
              const lastMessage = thread.lastMessage;
              const preview = truncate(lastMessage?.message) || t('pages.messages.startConversation');
              return (
                <li key={thread.id}>
                  <MessageThreadCard
                    id={thread.id}
                    href={`/app/sitter/${sitter?.id}/messages/${parent?.id}`}
                    unread={!thread.read}
                    counterpartyName={parent?.firstName}
                    counterpartyAvatarUrl={parent?.pictureUrl}
                    label={lastMessage?.label}
                    preview={preview}
                    hasMessageBody={!!lastMessage?.message}
                  />
                </li>
              );
            })}
          </ul>

          <Pagination
            page={page}
            pageCount={totalPages}
            firstPage={page <= 1}
            lastPage={page >= totalPages}
            onPageChange={setPage}
            namespace={namespaces.caregiver}
          />
        </div>
      )}
    </div>
  );
});
